import type { LobbyPlayer } from "@rosette/ts/src/business/lobby/LobbyPlayer.js";
import { HumanLobbyPlayer } from "@rosette/ts/src/business/lobby/HumanLobbyPlayer.js";
import { BotLobbyPlayer } from "@rosette/ts/src/business/lobby/BotLobbyPlayer.js";
import React from "react";
import type {
    APIPublicGamePreferences,
    APIUser,
} from "@rosette/ts/src/business/api/api_schema.js";
import { useFormatDate } from "@rosette/web_utils/src/time/useFormatDate.js";
import type { StaticImageData } from "next/image";
import Image from "next/image";
import { getRoleIcon } from "@/components/icon/getRoleIcon.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { PopupButton } from "@/components/generic/popup/PopupButton.js";
import { getBotIcon } from "@/components/icon/getBotIcon.js";
import type { TagProps } from "@/components/user/Tags.js";
import { createTagProps, Tags } from "@/components/user/Tags.js";
import { getPopupBadgeTags } from "@/utils/badges/badgeDisplay.js";
import { getNameColour } from "@rosette/web_components/src/user/getNameColour.js";
import { DisplayName } from "@rosette/web_components/src/user/DisplayName.js";
import Link from "next/link";
import { ReactionPlayback } from "@/components/game/reactions/ReactionPlayback.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { cn } from "@rosette/ts/src/util/cn.js";


interface NamedLink {
    name: string;
    href: string;
}


function PlayerDisplayName({ children }: { children: React.ReactNode }) {
    return (
        <p className="text-strong font-bold m-0">
            {children}
        </p>
    );
}


interface TemplatePlayerDescProps {
    icon: StaticImageData;
    iconAlt: string;
    preferences: APIPublicGamePreferences | null;
    roles: Role[];
    displayName: string;
    username?: string;
    desc: string;
    tags: TagProps[];
    links: NamedLink[];
    openLinksInNewTab: boolean;
}


export function TemplatePlayerDesc({
    icon, iconAlt, preferences, roles, displayName,
    username, desc, tags, links, openLinksInNewTab,
}: TemplatePlayerDescProps) {

    const linkElems = [];
    for (const link of links) {
        linkElems.push(
            <li key={link.name} className="mt-4 mb-3">
                <Link
                    href={link.href}
                    {...(openLinksInNewTab ? { target: "_blank" } : {})}>

                    {link.name}
                </Link>
            </li>,
        );
    }

    return (
        <>
            <div className="flex flex-row gap-2 items-center pb-4">
                <div className="max-w-[2.8em]">
                    <Image src={icon} alt={iconAlt} />
                </div>
                <div>
                    <PlayerDisplayName>
                        <DisplayName
                            name={displayName}
                            preferences={preferences}
                            roles={roles} />
                    </PlayerDisplayName>
                    {username && (
                        <p className="text-subtle m-0">
                            {username}
                        </p>
                    )}
                </div>
            </div>
            <p className="m-0">
                {desc}
            </p>
            <Tags tags={tags} />
            <ul className="m-0 list-disc pl-[1.35em] text-base leading-[1.4]">
                {linkElems}
            </ul>
        </>
    );
}


export function AnonymousPlayerDesc() {
    return (
        <PlayerDisplayName>
            Anonymous Player
        </PlayerDisplayName>
    );
}


interface HumanPlayerDescProps {
    user: APIUser;
    preferences: APIPublicGamePreferences | null;
    openLinksInNewTab: boolean;
}


export function HumanPlayerDesc({
    user, preferences, openLinksInNewTab,
}: HumanPlayerDescProps) {

    const roles = Role.list(user);
    const primaryRole = roles[roles.length - 1];
    const roleTags: TagProps[] = [];
    for (const role of roles) {
        if (role === Role.STANDARD)
            continue;

        roleTags.push(createTagProps(role));
    }
    // Badge pills sit alongside the role pills (e.g. "Admin") in one row.
    const tags = [...roleTags, ...getPopupBadgeTags(user.badges)];
    const joinDate = useFormatDate(user.createdAt, { verb: "Joined" });
    return (
        <TemplatePlayerDesc
            icon={getRoleIcon(primaryRole)}
            iconAlt={primaryRole.getDesc()}
            preferences={preferences}
            roles={roles}
            displayName={user.displayName}
            username={user.username}
            desc={joinDate}
            tags={tags}
            links={[
                { name: "View profile", href: `/u/${user.username}` },
                { name: "Report user", href: "/contact" },
            ]}
            openLinksInNewTab={openLinksInNewTab} />
    );
}


interface BotPlayerDescProps {
    player: BotLobbyPlayer;
    openLinksInNewTab: boolean;
}


export function BotPlayerDesc({ player, openLinksInNewTab }: BotPlayerDescProps) {
    const botType = player.getBotType();
    return (
        <TemplatePlayerDesc
            icon={getBotIcon(botType)}
            iconAlt={botType.getDesc()}
            preferences={null}
            roles={[]}
            displayName={botType.getLongName()}
            desc={botType.getDesc()}
            tags={[]}
            links={[]}
            openLinksInNewTab={openLinksInNewTab} />
    );
}


interface PlayerDescProps {
    player: LobbyPlayer;
    preferences: APIPublicGamePreferences;
    openLinksInNewTab: boolean;
}


export function PlayerDesc({ player, preferences, openLinksInNewTab }: PlayerDescProps) {
    if (player instanceof HumanLobbyPlayer) {
        const user = player.getUserOrNull();
        if (user === null)
            return (<AnonymousPlayerDesc />);

        return (
            <HumanPlayerDesc
                user={user}
                preferences={preferences}
                openLinksInNewTab={openLinksInNewTab} />
        );

    } else if (player instanceof BotLobbyPlayer) {
        return (
            <BotPlayerDesc
                player={player}
                openLinksInNewTab={openLinksInNewTab} />
        );

    } else {
        return (
            <PlayerDisplayName>
                Anonymous Player
            </PlayerDisplayName>
        );
    }
}


export function getPlayerDisplayName(player: LobbyPlayer | null | undefined): string {
    if (!player)
        return "Anonymous";

    if (player instanceof BotLobbyPlayer)
        return player.getBotType().getLongName();

    const gameData = player.getGameData();
    const anonymousName = (
        gameData !== null ? gameData.getPlayerType().getName() : "Anonymous"
    );
    if (!(player instanceof HumanLobbyPlayer))
        return anonymousName;

    const user = player.getUserOrNull();
    if (user === null)
        return anonymousName;

    return user.displayName;
}


interface PlayerDescPopupButtonProps {
    player: LobbyPlayer;
    yourPlayer: PlayerType | null;
    positionLeft: boolean;
    playerConnected: boolean;
    preferences: APIPublicGamePreferences;
    hideReactions?: boolean;
    outOfGame?: boolean;
    popupButtonClassName?: string;
    buttonClassName?: string;
    buttonPlayerNameClassName?: string;
    activePlayerNameClassName?: string;
}


export function PlayerDescPopupButton({
    player, yourPlayer, positionLeft, playerConnected,
    preferences, hideReactions, outOfGame,
    popupButtonClassName, buttonClassName, buttonPlayerNameClassName, activePlayerNameClassName,
}: PlayerDescPopupButtonProps) {
    const playerName = getPlayerDisplayName(player);
    const nameColour = getNameColour(preferences.nameColour);
    return (
        <PopupButton
            buttonClassName={popupButtonClassName}
            darkMode={true}
            contentClassName="min-w-[300px]"
            minPopupWidthPx={300}
            buttonContent={(
                <div className={buttonClassName}>
                    <div
                        className={cn(
                            "game-text",
                            buttonPlayerNameClassName ?? null,
                        )}
                        title={playerConnected ? undefined : "Player has Disconnected"}
                        style={{ color: nameColour.colorCSS }}>

                        {playerName}

                        {!outOfGame && (
                            <span
                                className={activePlayerNameClassName}
                                style={{ color: nameColour.lightColorCSS }}>

                                {playerName}
                            </span>
                        )}
                    </div>

                    {!outOfGame && (
                        <ReactionPlayback
                            player={player.getPlayerType()}
                            yourPlayer={yourPlayer}
                            positionLeft={positionLeft}
                            disabled={!!hideReactions} />
                    )}
                </div>
            )}>

            <PlayerDesc
                player={player}
                preferences={preferences}
                openLinksInNewTab={!outOfGame} />
        </PopupButton>
    );
}
